import { Box, SimpleGrid, VStack } from '@chakra-ui/react';
import { HeartHandshake, MapPin, MessageSquare, RotateCcw, Shield, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { FeaturePill, type FeaturePillTone } from '../FeaturePill/FeaturePill';
import { SectionHeader } from '../SectionHeader/SectionHeader';

interface Feature {
  id: string;
  labelId: string;
  defaultLabel: string;
  icon: LucideIcon;
  highlighted?: boolean;
  tone?: FeaturePillTone;
}

// Order is the design's visual arrangement: in the RTL grid each row fills
// right-to-left, so row 1 is تجارب / استرداد(accent) / دفع and row 2 is
// شراكات(soft) / خدمة / تجربة سلسة(soft).
const FEATURES: Feature[] = [
  {
    id: 'fastRefund',
    labelId: 'about.why.feature.fastRefund',
    defaultLabel: 'Fast refunds',
    icon: RotateCcw,
    highlighted: true,
    tone: 'accent',
  },
  {
    id: 'everywhere',
    labelId: 'about.why.feature.everywhere',
    defaultLabel: 'Experiences everywhere',
    icon: MapPin,
  },
  {
    id: 'securePayment',
    labelId: 'about.why.feature.securePayment',
    defaultLabel: 'Secure & trusted payment',
    icon: Shield,
  },
  {
    id: 'smoothExperience',
    labelId: 'about.why.feature.smoothExperience',
    defaultLabel: 'Smooth experience',
    icon: Zap,
    highlighted: true,
    tone: 'soft',
  },
  {
    id: 'exclusivePartnerships',
    labelId: 'about.why.feature.exclusivePartnerships',
    defaultLabel: 'Exclusive partnerships',
    icon: HeartHandshake,
    highlighted: true,
    tone: 'soft',
  },
  {
    id: 'customerService',
    labelId: 'about.why.feature.customerService',
    defaultLabel: 'Always-on customer service',
    icon: MessageSquare,
  },
];

/**
 * About page "Why Ticket Zone" section (TZ-386): the "ليش تكت زون؟" centered header
 * over a grid of six FeaturePills (one highlighted). Grid reflows 3 → 2 → 1 column
 * across breakpoints. RTL, on a soft off-white band.
 */
export const WhyTicketZoneSection = () => (
  <Box as='section' dir='rtl' bg='#FAFAF9' aria-labelledby='about-why-heading'>
    <VStack
      w='100%'
      maxW='container.xl'
      mx='auto'
      px={{ base: '16px', md: '100px' }}
      py={{ base: '48px', md: '80px' }}
      spacing={{ base: '32px', md: '48px' }}
      align='stretch'
    >
      <SectionHeader
        titleId='about-why-heading'
        align='center'
        eyebrow={<FormattedMessage id='about.why.eyebrow' defaultMessage='Our values' />}
        eyebrowProps={{
          fontWeight: '700',
          fontSize: '16px',
          lineHeight: '140%',
          letterSpacing: '-0.01em',
        }}
        title={<FormattedMessage id='about.why.title' defaultMessage='Why Ticket Zone?' />}
        titleProps={{
          fontWeight: '900',
          fontSize: '32px',
          lineHeight: '110%',
          letterSpacing: '-0.02em',
        }}
      />

      <SimpleGrid columns={{ base: 2, lg: 3 }} spacing={{ base: '12px', md: '16px', lg: '24px' }}>
        {FEATURES.map(({ id, labelId, defaultLabel, icon, highlighted, tone }) => (
          <FeaturePill
            key={id}
            icon={icon}
            highlighted={highlighted}
            tone={tone}
            label={<FormattedMessage id={labelId} defaultMessage={defaultLabel} />}
          />
        ))}
      </SimpleGrid>
    </VStack>
  </Box>
);
